import React, { useState } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { List, ListItem, makeStyles } from '@material-ui/core';
import CreateButton from 'components/Buttons/CreateButton';
import { notify } from 'components/Notifier';
import client from 'services/client';
import { Material, MaterialEditFormInterface, SubMaterial } from 'types';
import { transformId } from 'services/apiPlatformTransformer';
import SubMaterialItem from './SubMaterialItem';

type SubMaterialListProps = {
  subMaterials: Array<SubMaterial>;
  isEditing: boolean;
  control: Control<MaterialEditFormInterface, object>;
  setValue: UseFormSetValue<MaterialEditFormInterface>;
  fields: any;
  remove: (index: number) => void;
  append: any;
  material: Material;
};

const useSubMaterialListStyles = makeStyles({
  cellStyleSubMaterial20: {
    width: '20%',
    padding: '0 25px',
    fontWeight: 'bold',
  },
  cellStyleSubMaterial30: {
    width: '30%',
    paddingLeft: '25px',
  },
  createSubMaterial: {
    color: 'black',
  },
  createIconSubMaterial: {
    width: 20,
    height: 20,
  },
});

const SubMaterialList: React.FC<SubMaterialListProps> = ({
  subMaterials,
  isEditing,
  control,
  setValue,
  fields,
  remove,
  append,
  material,
}) => {
  const classes = useSubMaterialListStyles();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'materials' });
  const [isLimitSubMaterial, setIsLimitSubMaterial] = useState(false);
  const materialStandard = material.standard;

  const handleDeleteSubMaterial = async (subMaterial: SubMaterial, index: number) => {
    if (!subMaterial['@id']) {
      remove(index);
    } else {
      const path = `/sub_materials/${transformId(subMaterial['@id'])}`;
      try {
        await client.delete(path);
        remove(index);
        notify({
          message: pt('form.delete.success', {
            subMaterialName: subMaterial.name,
            materialName: material.shortName,
          }),
          severity: 'success',
        });
      } catch (e) {
        notify({
          message: pt('form.update.error'),
          severity: 'error',
        });
      }
    }
  };

  const handleAddSubMaterial = () => {
    if (fields.length <= 10) {
      append({ name: '' });
      setIsLimitSubMaterial(false);
    } else {
      setIsLimitSubMaterial(true);
    }
  };

  return (
    <List component="div" disablePadding>
      {subMaterials.map((subMaterial: SubMaterial, index: number) => (
        <SubMaterialItem
          key={index}
          subMaterial={subMaterial}
          index={index}
          isEditing={isEditing}
          isSubMaterial={materialStandard.isSubMaterial}
          control={control}
          setValue={setValue}
          handleDeleteSubMaterial={() => handleDeleteSubMaterial(subMaterial, index)}
        />
      ))}
      {isEditing && materialStandard.isSubMaterial && (
        <ListItem>
          <div className={classes.cellStyleSubMaterial20}>{fields.length === 0 ? pt('subMaterial.title') : ''}</div>
          <div className={classes.cellStyleSubMaterial30}>
            {!isLimitSubMaterial && fields.length < 10 && (
              <CreateButton
                onClick={handleAddSubMaterial}
                customClasses={classes.createSubMaterial}
                customClassesIcon={classes.createIconSubMaterial}
                icon={null}
                label={pt('pageContent.createButtonSubMaterialLabel')}
              />
            )}
          </div>
        </ListItem>
      )}
    </List>
  );
};

export default SubMaterialList;
